import { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { IconChevronDown } from "@tabler/icons-react";
import { Listbox } from "@headlessui/react";
import { Control, Controller } from "react-hook-form";
import useCallApiAndLoad from "../../../hooks/useCallApiAndLoad";
import { fetchResponseAdapter } from "../../../adapters/fetchAdapter";
import { getCodesCountries } from "../../../services/publicService";

interface PhoneData {
  name: string;
  code: string;
  flag: string;
  phone_length: number;
  phone_area_code: string;
  phone_mask: string;
  
}

interface Props {
  name: string;
  control: Control<any>;
  onCountryCodeChange?: (code: string) => void;
  onCountryLengthChange?: (length: number) => void;
  className?: string;
  defaultCode?: string; // <-- agregado
  defaultPhone?: string;

}

export default function PhoneSelector({
  name,
  control,
  onCountryCodeChange,
  onCountryLengthChange,
  className, defaultCode,
  defaultPhone

}: Props) {
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [selected, setSelected] = useState<PhoneData | undefined>(undefined);
  const [dataPhone, setDataPhone] = useState<PhoneData[]>([]);
  const [visualValue, setVisualValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
  const loadCountries = async () => {
    const { status, data } = await callEndpoint(getCodesCountries());
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const list = response.data;
      setDataPhone(list);

      // tu default: primer país del array
      const fallback = list[0];

      // validar defaultCode recibido
      const cleanCode =
        defaultCode && defaultCode !== "-" && defaultCode !== ""
          ? defaultCode
          : null;

      let chosen = fallback;

      if (cleanCode) {
        const found = list.find(
          (c: PhoneData) => c.phone_area_code === cleanCode
        );
        if (found) {
          chosen = found;
        }
      }

      setSelected(chosen);
      onCountryCodeChange?.(chosen.phone_area_code);
      onCountryLengthChange?.(chosen.phone_length);
    }
  };

  loadCountries().catch(console.error);
}, [defaultCode]);

useEffect(() => {
  if (defaultPhone && selected?.phone_mask) {
    // Rellenar el número al formato visual usando PatternFormat
    const mask = selected.phone_mask;

    // PatternFormat no ofrece formateo manual, así que
    // rellenamos en orden los dígitos del defaultPhone
    let formatted = "";
    let i = 0;

    for (let char of mask) {
      if (char === "#") {
        formatted += defaultPhone[i] ?? "_";
        i++;
      } else {
        formatted += char;
      }
    }

    setVisualValue(formatted);
  }
}, [defaultPhone, selected]);

  const handleCountryChange = (value: PhoneData) => {
    setSelected(value);
    onCountryCodeChange?.(value.phone_area_code);
    onCountryLengthChange?.(value.phone_length);
    setSearchTerm(""); // limpia búsqueda al seleccionar
  };

  const filteredCountries = dataPhone.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.phone_area_code.includes(searchTerm)
  );

  return (
    <div className={`mx-auto relative w-full ${className || ''}`}>
      <div className="flex rounded-xl border border-[#e0e0e0]">
        {/* Country selector */}
        <Listbox value={selected} onChange={handleCountryChange}>
          <div className="relative w-48">
            <Listbox.Button className="flex items-center gap-2 w-full h-full bg-white px-3 py-2 text-sm text-left focus:outline-none rounded-xl">
              {selected?.flag && (
                <img
                  src={selected.flag}
                  alt=""
                  className="h-5 w-5 rounded-full object-cover object-center"
                />
              )}
              <span className="truncate">{selected?.phone_area_code}</span>
              <IconChevronDown className="h-4 w-4 text-gray-400 ml-auto" />
            </Listbox.Button>

            <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-[220px] lg:w-full overflow-auto bg-white text-sm shadow-md border border-gray-200 rounded-md">
              <div className="sticky top-0 bg-white p-2 z-10">
                <input
                  type="text"
                  placeholder="Buscar país o código"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border px-2 py-1 text-sm rounded focus:outline-none"
                />
              </div>

              {filteredCountries.length === 0 && (
                <div className="px-3 py-2 text-gray-500">No se encontraron resultados</div>
              )}

              {filteredCountries.map((country, idx) => (
                <Listbox.Option
                  key={idx}
                  value={country}
                  className={({ active }) =>
                    `flex items-center gap-2 px-3 py-2 cursor-pointer ${
                      active ? "bg-blue-100" : ""
                    }`
                  }
                >
                  <img
                    src={country.flag}
                    alt=""
                    className="h-5 w-5 rounded-full object-cover object-center"
                  />
                  <span className="text-sm truncate">{country.name}</span>
                  <span className="ml-auto text-[#757575]">
                    {country.phone_area_code}
                  </span>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>

        {/* Phone input */}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <PatternFormat
              format={selected?.phone_mask || "###"}
              allowEmptyFormatting
              mask="_"
              placeholder={selected?.phone_mask || ""}
              className="flex-1 px-3 py-2.5 border-grayForm rounded-xl text-sm focus:outline-none"
              value={visualValue} // lo que se muestra en el input
              onValueChange={(values) => {
                setVisualValue(values.formattedValue); // lo visual (con espacios)
                field.onChange(values.value); // lo que se guarda (sin espacios)
              }}
            />
          )}
        />
      </div>
    </div>
  );
}
